import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import Drawer from '../Drawer';
import { LoaderSpinner } from '../LoaderSpinner';
import { askReportAssistant, saveCustomReport } from '../../slices/customReports';

/** Composer stops growing here and scrolls instead, so the thread never gets squeezed out. */
const MAX_INPUT_HEIGHT = 160;

const INTRO_MESSAGE =
  "Hi, I'm Kip. Describe the report you want and I'll build it from Eadin's data — for example: *\"Klaviyo revenue by day with conversion rate\"* or *\"Payments by PO with just the balance and total columns\"*.";

/**
 * Side prompt box for building custom reports with Kip. The user chats, Kip proposes a
 * report definition (base report + columns), and the user reviews and saves it.
 */
export const KipReportBuilderSidebar = ({
  isOpen,
  onClose,
  marginFromSidebar,
  onReportCreated,
  initialProposal = null,
}) => {
  const dispatch = useDispatch();
  const [messages, setMessages] = useState([{ role: 'assistant', content: INTRO_MESSAGE }]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setProposal(initialProposal ? { ...initialProposal } : null);
    } else {
      setProposal(null);
    }
  }, [initialProposal, isOpen]);

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, proposal, isThinking]);

  // Grow the composer with the text (one row up to MAX_INPUT_HEIGHT, then scroll internally).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_INPUT_HEIGHT)}px`;
  }, [input, isOpen]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const prompt = input.trim();
    if (!prompt || isThinking) return;

    const nextMessages = [...messages, { role: 'user', content: prompt }];
    setMessages(nextMessages);
    setInput('');
    setIsThinking(true);
    try {
      // Skip the canned intro so the model only sees the real conversation.
      const payload = nextMessages.filter((m) => m.content !== INTRO_MESSAGE);
      const data = await dispatch(
        askReportAssistant({ messages: payload, proposal }),
      ).unwrap();
      setMessages((prev) => [...prev, { role: 'assistant', content: data?.reply || '...' }]);
      if (data?.proposal) {
        setProposal((prev) => ({
          ...(prev || {}),
          ...data.proposal,
          id: prev?.id ?? data.proposal?.id,
          prompt,
        }));
      } else if (data?.proposalErrors?.length) {
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: `I drafted a report but it didn't pass validation:\n${data.proposalErrors
              .map((e) => `- ${e}`)
              .join('\n')}\n\nCould you adjust your request?`,
          },
        ]);
      }
    } catch (err) {
      const message = typeof err === 'string' ? err : 'Kip could not process this request. Please try again.';
      setMessages((prev) => [...prev, { role: 'assistant', content: message }]);
    } finally {
      setIsThinking(false);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  };

  const handleSave = async () => {
    if (!proposal || isSaving) return;
    setIsSaving(true);
    try {
      const { report } = await dispatch(saveCustomReport(proposal)).unwrap();
      toast.success(proposal.id ? 'Report updated' : 'Report created', { position: 'bottom-right' });
      onReportCreated?.(report);
    } catch (err) {
      toast.error(typeof err === 'string' ? err : 'Failed to save report', { position: 'bottom-right' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      marginFromSidebar={marginFromSidebar}
      title={initialProposal ? 'Edit report with Kip' : 'Create report with Kip'}
      width="420px"
      showActionButtons={false}
      bodyClassName="flex flex-col"
    >
      <div className="flex h-full flex-col">
        <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white">
            <img src="/kip-mark.png" alt="" className="h-7 w-7 object-contain" />
          </span>
          <div>
            <p className="text-sm font-semibold text-gray-900">Kip</p>
            <p className="text-xs text-gray-500">Builds custom reports from Eadin data (read-only)</p>
          </div>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {messages.map((message, index) => (
            <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${
                  message.role === 'user'
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                <div className="prose prose-sm max-w-none [&_p]:m-0 [&_ul]:my-1">
                  <ReactMarkdown>{message.content}</ReactMarkdown>
                </div>
              </div>
            </div>
          ))}
          {isThinking && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <LoaderSpinner width="18" height="18" border="2" colorSpinner="black" />
              Kip is thinking…
            </div>
          )}

          {proposal && (
            <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Proposed report</p>
              <p className="mt-1 text-sm font-semibold text-gray-900">{proposal.name}</p>
              {proposal.description && (
                <p className="mt-1 text-xs text-gray-600">{proposal.description}</p>
              )}
              <p className="mt-2 text-xs text-gray-500">
                Based on: <span className="font-medium text-gray-700">{proposal.baseReportId}</span>
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {proposal.columns.map((column) => (
                  <span
                    key={column}
                    className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-700"
                  >
                    {column}
                  </span>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="inline-flex items-center rounded-md bg-black px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-60"
                >
                  {isSaving ? 'Saving…' : 'Save report'}
                </button>
                <button
                  type="button"
                  onClick={() => setProposal(null)}
                  className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSubmit} className="border-t border-gray-200 px-3 pb-6 pt-3">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              rows={1}
              placeholder="Describe the report you want…"
              className="flex-1 resize-none overflow-y-auto rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-gray-900"
              style={{ maxHeight: MAX_INPUT_HEIGHT }}
              disabled={isThinking}
            />
            <button
              type="submit"
              disabled={!input.trim() || isThinking}
              aria-label="Send"
              className="flex h-9 w-9 items-center justify-center rounded-md bg-black text-white hover:bg-gray-800 disabled:opacity-50"
            >
              <PaperAirplaneIcon className="h-4 w-4" />
            </button>
          </div>
        </form>
      </div>
    </Drawer>
  );
};
